import { useState } from "react";
import type { PlanPart, TreatmentPlan } from "@dentacore/shared-types";

import { correctStep, uncompleteStep } from "../api/plans";
import { Modal } from "../ui/modal";
import { RequiredMark } from "../ui/required-mark";
import { formatMoney, minorUnitsPerUnit, parseMoney } from "./money";
import styles from "./correct-visit-dialog.module.css";

/**
 * Correct a finished visit — PP-D5 and PP-D6.
 *
 * **A finished step is never removed**, but its tooth and price can be put
 * right, or its completion undone when the work never happened, **while its
 * invoice is still a draft** — and only with a reason, which goes on the audit
 * row. Once the invoice is issued the server refuses and says to void it; the
 * refusal is shown here as it arrives, because only the server knows which
 * invoices have gone out.
 *
 * One reason for the whole dialog: it is one correction, made by one person
 * looking at one visit.
 */
export function CorrectVisitDialog({
  plan,
  part,
  currency,
  onClose,
  onSaved,
}: {
  plan: TreatmentPlan;
  part: PlanPart;
  currency: string;
  onClose: () => void;
  onSaved: (plan: TreatmentPlan) => void;
}): React.JSX.Element {
  const done = part.items.filter((item) => item.status === "COMPLETED");
  const [teeth, setTeeth] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      done.map((item) => [item.id, item.fdiNumber === null ? "" : String(item.fdiNumber)]),
    ),
  );
  const [prices, setPrices] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      done.map((item) => [
        item.id,
        ((item.priceCents ?? 0) / minorUnitsPerUnit(currency)).toString(),
      ]),
    ),
  );
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const why = reason.trim();
  const visitNumber = plan.parts.indexOf(part) + 1;

  const run = async (work: () => Promise<TreatmentPlan>): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      onSaved(await work());
      onClose();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "That could not be saved");
    } finally {
      setBusy(false);
    }
  };

  const save = async (): Promise<TreatmentPlan> => {
    let latest = plan;
    for (const item of done) {
      const fdi = item.fdiNumber === null ? undefined : Number(teeth[item.id]);
      const price = parseMoney(prices[item.id] ?? "", currency);
      const changed: { fdiNumber?: number; priceCents?: number } = {};
      if (fdi !== undefined && fdi !== item.fdiNumber) changed.fdiNumber = fdi;
      if (price !== null && price !== item.priceCents) changed.priceCents = price;
      if (Object.keys(changed).length === 0) continue;
      latest = await correctStep(plan.id, part.id, item.id, {
        ...changed,
        ...(changed.priceCents === undefined ? {} : { overrideNote: why }),
        reason: why,
      });
    }
    return latest;
  };

  return (
    <Modal label={`Correct visit ${String(visitNumber)}`} testId="correct-visit" onClose={onClose}>
      <header className={styles.header}>
        <h2 className={styles.title}>Correct visit {visitNumber}</h2>
        <button type="button" className={styles.close} onClick={onClose}>
          Close
        </button>
      </header>
      <p className={styles.intro}>
        Fix a tooth or a price, or undo a step that was not done. This works until the invoice for
        it is issued; after that, void the invoice instead.
      </p>

      <ul className={styles.steps}>
        {done.map((item) => (
          <li key={item.id} className={styles.step} data-testid="correct-step">
            <span className={styles.label}>{item.label ?? "Step"}</span>
            {item.fdiNumber !== null && (
              <label className={styles.small}>
                <span className={styles.fieldLabel}>Tooth</span>
                <input
                  id={`correct-tooth-${item.id}`}
                  className={styles.input}
                  inputMode="numeric"
                  value={teeth[item.id] ?? ""}
                  onChange={(event) => setTeeth({ ...teeth, [item.id]: event.target.value })}
                  data-testid="correct-tooth"
                />
              </label>
            )}
            <label className={styles.small}>
              <span className={styles.fieldLabel}>Price</span>
              <input
                id={`correct-price-${item.id}`}
                className={styles.input}
                inputMode="decimal"
                value={prices[item.id] ?? ""}
                onChange={(event) => setPrices({ ...prices, [item.id]: event.target.value })}
                aria-describedby={`correct-was-${item.id}`}
                data-testid="correct-price"
              />
              <span id={`correct-was-${item.id}`} className={styles.was}>
                was {formatMoney(item.priceCents ?? 0, currency)}
              </span>
            </label>
            <button
              type="button"
              className={styles.undo}
              disabled={busy || why === ""}
              onClick={() => void run(() => uncompleteStep(plan.id, part.id, item.id, why))}
              data-testid="correct-undo"
            >
              Not done
            </button>
          </li>
        ))}
      </ul>

      <label className={styles.field}>
        <span className={styles.fieldLabel}>
          Why
          <RequiredMark />
        </span>
        <input
          id="correct-reason"
          className={styles.input}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          placeholder="Wrong tooth typed"
          data-testid="correct-reason"
        />
      </label>

      {error !== null && (
        <p className={styles.error} role="alert" data-testid="correct-error">
          {error}
        </p>
      )}

      <div className={styles.actions}>
        <button
          type="button"
          className={styles.save}
          disabled={busy || why === ""}
          onClick={() => void run(save)}
          data-testid="correct-save"
        >
          Save correction
        </button>
      </div>
    </Modal>
  );
}
